import { useState, useEffect, useCallback } from "react";
import { RefreshCw, X } from "lucide-react";
import { APP_VERSION } from "@/lib/version";
import { Button } from "@/components/ui/button";

const VERSION_CHECK_INTERVAL = 10 * 60 * 1000; // 10 perc

export const VersionUpdateNotifier = () => {
  const [newVersionAvailable, setNewVersionAvailable] = useState(false);
  const [newVersion, setNewVersion] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState(false);

  const checkForUpdates = useCallback(async () => {
    try {
      // Cache-busting query paraméterrel lekérjük a legújabb verziót
      const response = await fetch(`/version.json?t=${Date.now()}`);
      if (!response.ok) return;

      const data = await response.json();
      const serverVersion = data.version;

      // Ha a szerver verzió különbözik a betöltött verziótól
      if (serverVersion && serverVersion !== APP_VERSION) {
        setNewVersion(serverVersion);
        setNewVersionAvailable(true);
      }
    } catch (error) {
      // Csendben kezeljük a hibát - nem kritikus funkció
      console.debug("Verzió ellenőrzés sikertelen:", error);
    }
  }, []);

  useEffect(() => {
    // Első ellenőrzés 30 másodperc múlva (hogy ne lassítsa a kezdeti betöltést)
    const initialTimeout = setTimeout(checkForUpdates, 30 * 1000);

    // Rendszeres ellenőrzés
    const interval = setInterval(checkForUpdates, VERSION_CHECK_INTERVAL);

    return () => {
      clearTimeout(initialTimeout);
      clearInterval(interval);
    };
  }, [checkForUpdates]);

  const handleRefresh = () => {
    // Hard refresh - cache invalidálásával
    window.location.reload();
  };

  const handleDismiss = () => {
    setDismissed(true);
  };

  if (!newVersionAvailable || dismissed) {
    return null;
  }

  return (
    <div className="fixed bottom-4 left-4 z-50 animate-fade-in">
      <div className="bg-primary text-primary-foreground rounded-lg shadow-lg p-4 max-w-sm border border-primary/20">
        <div className="flex items-start gap-3">
          <div className="bg-primary-foreground/20 rounded-full p-2 flex-shrink-0">
            <RefreshCw className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="font-semibold text-sm mb-1">Új verzió érhető el!</h4>
            <p className="text-xs text-primary-foreground/80 mb-3">
              A NapiHami frissült ({APP_VERSION} → {newVersion}). Kattints a frissítéshez!
            </p>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="secondary"
                onClick={handleRefresh}
                className="bg-primary-foreground text-primary hover:bg-primary-foreground/90 text-xs"
              >
                <RefreshCw className="h-3 w-3 mr-1" />
                Frissítés
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={handleDismiss}
                className="text-primary-foreground/70 hover:text-primary-foreground hover:bg-primary-foreground/10 text-xs"
              >
                Később
              </Button>
            </div>
          </div>
          <button
            onClick={handleDismiss}
            className="text-primary-foreground/60 hover:text-primary-foreground transition-colors flex-shrink-0"
            aria-label="Bezárás"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};
